import React, { useEffect, useMemo, useState } from 'react'
import CommonCard from '../../../global/card'
import CommonSkeletonLoader from '../../../global/loading'
import { useLocation } from 'react-router-dom'
import SOURCES from '../../../../utils/constants'
import { useGenericQuery } from '../../../../queries/use-generic-query'
import { QUERY_KEYS } from '../../../../lib/query-constants'
import useDebounce from '../../../../utils/customHooks'
import EnhancedLeadsImportedUserList from './EnhancedLeadsImportedUserList'
import CommonPagination from '../../../global/pagination'
import EnhancedAdvanceSearchLeadsImportedUser from './EnhancedAdvanceSearchLeadsImportedUser'
import { useGenericMutation } from '../../../../queries/use-generic-mutation'
import { useQueryClient } from '@tanstack/react-query'

const EnhancedLeadsImportedUser = () => {
    const { DEFAULT_RECORDS_LISTING, DEFAULT_RECORDS_SEARCH } = SOURCES;
    const queryClient = useQueryClient();

    const [searchLeadsImported, setSearchLeadsImported] = useState("")
    const [leadsImportedViewType, setLeadsImportedViewType] = useState(() => {
        return localStorage.getItem("leadsImportedUserViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });

    // We don't need local state for detail view here as it is handled inside the list component
    // But if we wanted to control it from parent we could. 
    // The shared list component handles opening the drawer internally.

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        10
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [paginationSearch, setPaginationSearch] = useState({
        offsetSearch: 0,
        limitSearch: SOURCES.DEFAULT_RECORDS_SEARCH,
    });

    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const [advanceSearch, setAdvanceSearch] = useState({
        advance_search_enabled:
            SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        startDate: "",
        endDate: "",
        leadSource: "",
        searchTerm: "",
        user: "",
    });

    const debouncedSearchTerm = useDebounce(searchLeadsImported, 500);

    const { mutateAsync: saveHotLeads } = useGenericMutation({
        endpoint: `leadinfo/hotlead`,
    });

    // Key params for main listing
    const keyParamsForLeadInfo = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Key params for search
    const searchKeyParams = {
        offset: paginationSearch.offsetSearch,
        limit: paginationSearch.limitSearch,
        q: debouncedSearchTerm,
        advance_search: advanceSearch.advance_search_enabled,
        startdate: advanceSearch.startDate,
        enddate: advanceSearch.endDate,
        leadsource: advanceSearch.leadSource,
        term: advanceSearch.searchTerm,
        userEmail: advanceSearch.user
    };

    // Main leads data query
    const { data: leadsData, isLoading: leadsDataLoading, isFetching: leadsDataFetching, refetch: refetchLeads } = useGenericQuery({
        endpoint: "leadinfo",
        queryKey: QUERY_KEYS.leadsImportedUser.lists({ ...keyParamsForLeadInfo }),
        queryParams: { ...keyParamsForLeadInfo },
        onError: () => { },
        onSuccess: () => { },
    });

    // Search data query
    const { data: searchData, isInitialLoading: searchDataLoading, refetch: refetchSearch } = useGenericQuery({
        endpoint: "leadinfo/search",
        queryKey: QUERY_KEYS.leadsImportedUser.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        onError: () => { },
        onSuccess: () => { },
        enabled: !!debouncedSearchTerm.trim() ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)
    });

    const onToggleHotLead = async (lead) => {
        await saveHotLeads({ leadId: lead._id, isHotLead: !lead.isHotLead });
        // Since we are using React Query, we should invalidate queries to refresh data
        // For optimisitc updates or just simple refetch
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leadsImportedUser.lists({ ...keyParamsForLeadInfo }) });
    };

    // Determine final data to display
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm ||
            (advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER &&
                advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_CLOSED_SEARCH_FORM)) {
            return searchData?.results || [];
        }
        return leadsData?.results || [];
    }, [leadsData, searchData, debouncedSearchTerm, advanceSearch.advance_search_enabled]);

    const isLoading = leadsDataLoading || searchDataLoading;
    const dataExist = getFinalData && getFinalData.length > 0;

    useEffect(() => {
        setAdvanceSearch((prev) => ({
            ...prev,
            advance_search_enabled:
                SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        }));
        setShowAdvanceSearch(false);
    }, [searchLeadsImported]);

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search leads imported...",
                searchInput: searchLeadsImported,
                setSearchInput: setSearchLeadsImported
            }}
            isListGridView={{
                show: true,
                viewType: leadsImportedViewType,
                setViewType: setLeadsImportedViewType,
                viewFor: "leadsImportedUserViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={leadsImportedViewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            isAdvanceSearch={{
                show: true,
                children: <EnhancedAdvanceSearchLeadsImportedUser
                    isOpen={showAdvanceSearch}
                    advanceSearch={advanceSearch}
                    setShowAdvanceSearch={setShowAdvanceSearch}
                    setAdvanceSearch={setAdvanceSearch}
                    getAdvanceSearchData={() => { }}
                    showSearchTerm={true}
                />
            }}
            className={(!isLoading && leadsDataFetching) && "opacity-50"}
        >
            <EnhancedLeadsImportedUserList
                isLoading={isLoading}
                leadsImported={getFinalData}
                viewDetail={() => { }} // Handled internally by the list component
                sort={sort}
                setSort={setSort}
                viewType={leadsImportedViewType}
                onToggleHotLead={onToggleHotLead}
            />

            {(dataExist && !searchLeadsImported &&
                advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) &&
                leadsData?.totalReords > DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={leadsData?.totalReords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="Leads Imported"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    )
}

export default EnhancedLeadsImportedUser